import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import LightGestion from 'components/headers/lightGestion';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { Button, InputGroup, FormControl } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const EditStock = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [changes, setChanges] = useState({}); 
const { t } = useTranslation();

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/products', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data || []);
    } catch (e) {
      console.error(e);
      Swal.fire(t('stock.error', 'Erreur'), t('stock.fetchFailed', 'Impossible de récupérer les produits'));
    }
  }, [t]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const getFirst = useCallback((obj, keys) => {
    if (!obj) return undefined;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return undefined;
  }, []);

  const toNumber = useCallback((v) => {
    if (v === undefined || v === null || v === '') return NaN;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  }, []);

  const qtyOf = useCallback((p) => {
    const v = getFirst(p, ['StockQuantity', 'stock', 'Stock', 'quantity', 'StockQte']);
    const n = toNumber(v);
    return Number.isFinite(n) ? n : 0;
  }, [getFirst, toNumber]);

  const thresholdOf = useCallback((p) => {
    const v = getFirst(p, ['alert_threshold', 'SeuilAlerte', 'AlertThreshold', 'seuil_alerte']);
    const n = toNumber(v);
    return Number.isFinite(n) ? n : 0;
  }, [getFirst, toNumber]);

  const unitPriceOf = useCallback((p) => Number(getFirst(p, ['retail_price', 'PrixDetail', 'prix_detail', 'Prix_Detail'])) || 0, [getFirst]);

  const totalCount = products.length;
  const inStockCount = products.filter(p => qtyOf(p) > 0).length;
  const alertCount = products.filter(p => { const q = qtyOf(p); const thr = thresholdOf(p); return q > 0 && thr > 0 && q <= thr; }).length;
  const outCount = products.filter(p => qtyOf(p) <= 0).length;

  const visible = useMemo(() => products.filter(p => {
    const nameObj = getFirst(p, ['Name', 'name']);
    const name = (nameObj?.fr || nameObj?.en || nameObj || '').toString().toLowerCase();
    if (!name.includes(search.toLowerCase())) return false;
    const q = qtyOf(p);
    const thr = thresholdOf(p);
    if (filterMode === 'in') return q > 0;
    if (filterMode === 'alert') return q > 0 && thr > 0 && q <= thr;
    if (filterMode === 'out') return q <= 0;
    return true;
  }), [products, search, filterMode, qtyOf, thresholdOf, getFirst]);

  const modifiedCount = Object.keys(changes).length;

  const totalValue = useMemo(() => {
    return visible.reduce((sum, p) => {
      const id = p.ProductID || p.id || p.product_id;
      const q = qtyOf(p);
      const newStock = changes[id]?.newStock;
      const effective = (newStock !== undefined && newStock !== '') ? Number(newStock) : q;
      const price = unitPriceOf(p);
      return sum + (effective * price);
    }, 0).toFixed(2);
  }, [visible, changes, qtyOf, unitPriceOf]);

  const setNewStock = (id, value) => {
    setChanges(prev => {
      const copy = { ...prev };
      if (value === '' || value === null) {
        delete copy[id];
      } else {
        copy[id] = { ...(copy[id] || {}), newStock: value };
      }
      return copy;
    });
  };

  const resetChanges = () => setChanges({});

  

  const applyAdjustments = async () => {
const items = Object.keys(changes).map(id => ({ ProductID: Number(id), StockQuantity: Number(changes[id].newStock) }));
    console.log("Payload to backend:", items);
   
if (items.length === 0) return Swal.fire(t('stock.info', 'Info'), t('stock.noChanges', 'Aucune modification à sauvegarder'));

    try {
      const token = localStorage.getItem('authToken');
      await axios.post('https://plateformewmg.cloud/api/Gestion/products/update-stock', { items }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      Swal.fire(t('stock.success', 'Succès'), t('stock.saved', 'Ajustements sauvegardés'), 'success');
      setChanges({});
      fetchProducts();
    } catch (e) {
      console.error(e);
      Swal.fire(t('stock.error', 'Erreur'), JSON.stringify(e?.response?.data || e.message));
    }
  };

  return (
    <AnimationRevealPage>
      <LightGestion />

      <div className="container  app-content" style={{ maxWidth: 1100 ,marginTop:"-60px",marginLeft:"-20px"}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ fontWeight: 900, fontSize: '30px', marginBottom: 6 }}>{t('stock.title', 'Ajustement de Stock (Inventaire)')}</h2>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', color: '#6b7280' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={{ width: 10, height: 10, borderRadius: 10, background: '#2b6cb0', display: 'inline-block' }} /> {t('stock.total', 'Total')}: {totalCount}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={{ width: 10, height: 10, borderRadius: 10, background: '#28a745', display: 'inline-block' }} /> {t('stock.inStock', 'En stock')}: {inStockCount}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={{ width: 10, height: 10, borderRadius: 10, background: '#f6c84c', display: 'inline-block' }} /> {t('stock.alerts', 'Alertes')}: {alertCount}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={{ width: 10, height: 10, borderRadius: 10, background: '#ef4444', display: 'inline-block' }} /> {t('stock.outOfStock', 'Ruptures')}: {outCount}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#7c3aed' }}>{t('stock.value', 'Valeur')}: {totalValue} {t('stock.currency', 'MAD')}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#ff7a00' }}>{t('stock.modified', 'Modifiés')}: {modifiedCount}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <Button variant="outline-secondary" onClick={resetChanges} style={{ minWidth: 160 }}>{t('stock.reset', 'Réinitialiser')}</Button>
            <Button variant="success" onClick={applyAdjustments} style={{ minWidth: 220, fontWeight: 700 }}>{t('stock.saveAdjustments', 'Sauvegarder les Ajustements')}</Button>
          </div>
        </div>

        <div style={{ marginTop: 18, background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #eef2f7' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <InputGroup style={{ flex: 1 }}>
              <InputGroup.Text>🔍</InputGroup.Text>
              <FormControl placeholder={t('stock.searchPlaceholder', 'Rechercher un produit...')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </InputGroup>

            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant={filterMode === 'all' ? 'primary' : 'light'} onClick={() => setFilterMode('all')}>{t('stock.filters.all', 'Tous')}</Button>
              <Button variant={filterMode === 'in' ? 'primary' : 'light'} onClick={() => setFilterMode('in')}>{t('stock.filters.in', 'En stock')}</Button>
              <Button variant={filterMode === 'alert' ? 'primary' : 'light'} onClick={() => setFilterMode('alert')}>{t('stock.filters.alert', 'Alertes')}</Button>
              <Button variant={filterMode === 'out' ? 'primary' : 'light'} onClick={() => setFilterMode('out')}>{t('stock.filters.out', 'Ruptures')}</Button>
            </div>
          </div>

          

          <div style={{ borderTop: '1px solid #f3f4f6' }} />

          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 120px 120px 120px 140px', gap: 12, padding: '12px', alignItems: 'center', color: '#6b7280' }}>
              <div />
              <div>{t('stock.headers.product', 'Produit')}</div>
              <div style={{ textAlign: 'center' }}>{t('stock.headers.currentStock', 'Stock actuel')}</div>
              <div style={{ textAlign: 'center' }}>{t('stock.headers.threshold', 'Seuil')}</div>
              <div style={{ textAlign: 'center' }}>{t('stock.headers.newStock', 'Nouveau stock')}</div>
              <div style={{ textAlign: 'center' }}>{t('stock.headers.value', 'Valeur')}</div>
            </div>

            <div style={{ maxHeight: 420, overflow: 'auto' }}>
              {visible.map((p, idx) => {
                console.log(p);
                // Robust ID extraction to keep consistency across calculations and payload
                const id = p.ProductID || p.id || p.product_id || p.ID;
                const q = qtyOf(p);
                const thr = thresholdOf(p);
                const newStock = changes[id]?.newStock !== undefined ? changes[id].newStock : '';
              
                const price = unitPriceOf(p);
                const effective = (newStock !== '' ? Number(newStock) : q);
                const value = (price * effective).toFixed(2);
                const isChanged = newStock !== '' && Number(newStock) !== q;

                return (
                  <div key={id} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 120px 120px 120px 140px', gap: 12, padding: 12, alignItems: 'center', background: isChanged ? '#fff7e6' : 'transparent', borderLeft: isChanged ? '4px solid #f59e0b' : '4px solid transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#6b8cff', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(idx + 1)}</div>
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.Name?.fr || p.Name || p.name || ''}</div>
                      <div style={{ fontSize: 13, color: '#9ca3af' }}>{p.Category || p.Categorie || ''}</div>
                    </div>

                    <div style={{ textAlign: 'center', fontWeight: 800 }}>{q}</div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 10, background: q <= 0 ? '#ef4444' : (thr > 0 && q <= thr ? '#f6c84c' : '#28a745') }} />
                        <div style={{ fontSize: 13 }}>{q <= 0 ? t('stock.status.rupture', 'Rupture') : (thr > 0 && q <= thr ? t('stock.status.alert', 'Alerte') : t('stock.status.inStock', 'En stock'))}</div>
                      </div>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <input
                        type="number"
                        value={newStock}
                        onChange={(e) => setNewStock(id, e.target.value)}
                        style={{ width: '120px', padding: '10px 12px', borderRadius: 12, border: '1px solid #e6e6e9', textAlign: 'center' }}
                      />
                      {/* movement badge removed as requested */}
                    </div>

                    <div style={{ textAlign: 'center', fontWeight: 700 }}>{value} {t('stock.currency', 'MAD')}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </AnimationRevealPage>
  );
};

export default EditStock;
